import React, { useState, useEffect } from 'react';

interface RippleEffect {
    id: number;
    x: number;
    y: number;
}

interface ClickRippleProps {
    children: React.ReactNode;
    onClick?: (e: React.MouseEvent<HTMLDivElement>) => void;
    onMouseEnter?: (e: React.MouseEvent<HTMLDivElement>) => void;
    onMouseLeave?: (e: React.MouseEvent<HTMLDivElement>) => void;
    style?: React.CSSProperties;
    className?: string;
    rippleColor?: string;
    rippleDuration?: number;
}

const ClickRipple: React.FC<ClickRippleProps> = ({
    children,
    onClick,
    onMouseEnter,
    onMouseLeave,
    style,
    className,
    rippleColor = 'rgba(59, 130, 246, 0.5)', // Primary blue with opacity
    rippleDuration = 600
}) => {
    const [ripples, setRipples] = useState<RippleEffect[]>([]);

    const handleClick = (e: React.MouseEvent<HTMLDivElement>) => {
        const rect = e.currentTarget.getBoundingClientRect();
        const x = e.clientX - rect.left;
        const y = e.clientY - rect.top;

        const newRipple: RippleEffect = {
            id: Date.now(),
            x,
            y
        };

        setRipples(prev => [...prev, newRipple]);

        // Call the original onClick if provided
        if (onClick) {
            onClick(e);
        }
    };

    // Remove ripples after animation completes
    useEffect(() => {
        if (ripples.length === 0) return;

        const timer = setTimeout(() => {
            setRipples(prev => prev.slice(1));
        }, rippleDuration);

        return () => clearTimeout(timer);
    }, [ripples, rippleDuration]);

    return (
        <div
            onClick={handleClick}
            onMouseEnter={onMouseEnter}
            onMouseLeave={onMouseLeave}
            style={{
                position: 'relative',
                overflow: 'hidden',
                isolation: 'isolate',
                ...style
            }}
            className={className}
        >
            {children}

            {/* Ripple Effects */}
            {ripples.map((ripple) => (
                <span
                    key={ripple.id}
                    style={{
                        position: 'absolute',
                        left: ripple.x,
                        top: ripple.y,
                        width: '0',
                        height: '0',
                        borderRadius: '50%',
                        backgroundColor: rippleColor,
                        transform: 'translate(-50%, -50%)',
                        animation: `ripple-expand ${rippleDuration}ms ease-out`,
                        pointerEvents: 'none',
                        zIndex: 0
                    }}
                />
            ))}

            <style>{`
                @keyframes ripple-expand {
                    0% {
                        width: 0;
                        height: 0;
                        opacity: 1;
                    }
                    100% {
                        width: 300px;
                        height: 300px;
                        opacity: 0;
                    }
                }
            `}</style>
        </div>
    );
};

export default ClickRipple;
